import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import panelStyles from '../panelStyles';
import {formatMoney} from '../panelMetrics';

/**
 * Gunluk rezervasyon adedi.
 * Inline SVG: veri az ve statik, grafik kutuphanesi yuku gereksiz.
 *
 * viewBox yuksekligi cizim yuksekligiyle ayni oldugu icin dikey eksende
 * 1 birim = 1 piksel; ipucu kartini y degerine gore konumlandirabiliyoruz.
 * Yatayda preserveAspectRatio="none" ile esniyor, o yuzden x'i orana ceviriyoruz.
 */
export default function TrendChart({trend, minHeight = 150, color = '#d71f26'}) {
    const {t} = useTranslation();
    const classes = panelStyles();
    const [hover, setHover] = useState(null);
    const wrapRef = useRef(null);

    // Kart, komsusuyla ayni yuksekliğe gerildigi icin grafik alani degisken.
    // Olculup viewBox'a yaziliyor: dikeyde 1 birim = 1 piksel kaliyor, boylece
    // cizgi kalinligi ve ipucu konumu olcekten etkilenmiyor.
    const [height, setHeight] = useState(minHeight);

    useEffect(() => {
        const el = wrapRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;

        const measure = () => setHeight(Math.max(minHeight, Math.round(el.clientHeight)));
        measure();

        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [minHeight]);

    const points = (trend && trend.points) || [];

    if (points.length < 2) {
        return <div className={classes.emptyBox}>{t('enw-panel-chart_empty', 'Grafik için yeterli veri yok.')}</div>;
    }

    const width = 720;
    const top = 8;
    const bottom = height - 6;
    // Tepe degeri en az 1: tum degerler 0 iken cizgi tabana yapissin, patlamasin
    const max = Math.max(1, trend.max);

    const coords = points.map((point, index) => {
        const x = (index / (points.length - 1)) * width;
        const y = bottom - (point.count / max) * (bottom - top);
        return {x, y, ...point};
    });

    const line = coords.map((c) => `${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(' L');
    const last = coords[coords.length - 1];
    const gridLines = [0, 1, 2, 3].map((i) => top + ((bottom - top) / 3) * i);

    const track = (event) => {
        const box = event.currentTarget.getBoundingClientRect();
        if (!box.width) return;
        const ratio = Math.min(1, Math.max(0, (event.clientX - box.left) / box.width));
        setHover(Math.round(ratio * (points.length - 1)));
    };

    const active = hover !== null ? coords[hover] : null;
    const ratio = active ? active.x / width : 0;

    return (
        <>
            <div
                ref={wrapRef}
                className={classes.chartWrap}
                onMouseMove={track}
                onMouseLeave={() => setHover(null)}
            >
                <svg
                    viewBox={`0 0 ${width} ${height}`}
                    preserveAspectRatio="none"
                    style={{position: 'absolute', top: 0, left: 0, width: '100%', height: '100%'}}
                    role="img"
                    aria-label={t('enw-panel-chart_alt', 'Günlük rezervasyon adedi')}
                >
                    <defs>
                        <linearGradient id="panelTrendArea" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={color} stopOpacity="0.2" />
                            <stop offset="100%" stopColor={color} stopOpacity="0" />
                        </linearGradient>
                    </defs>

                    <g stroke="#eef1f6" strokeWidth="1">
                        {gridLines.map((y) => (
                            <line key={y} x1="0" y1={y} x2={width} y2={y} />
                        ))}
                    </g>

                    <path fill="url(#panelTrendArea)" d={`M${line} L${width},${height} L0,${height} Z`} />
                    <path fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" d={`M${line}`} />

                    {active && (
                        <line
                            x1={active.x} y1={top - 4} x2={active.x} y2={height}
                            stroke="#c9d3e0" strokeWidth="1" strokeDasharray="3 3"
                            vectorEffect="non-scaling-stroke"
                        />
                    )}

                    {/* Son gunun noktasi sabit; imlec baska bir gundeyken gizlenir */}
                    {(!active || active.x !== last.x) && (
                        <circle cx={last.x} cy={last.y} r="4" fill={color} stroke="#fff" strokeWidth="2" />
                    )}
                    {active && (
                        <circle cx={active.x} cy={active.y} r="4.5" fill={color} stroke="#fff" strokeWidth="2" />
                    )}
                </svg>

                {active && (
                    <div
                        className={classes.chartTip}
                        style={{
                            left: `${ratio * 100}%`,
                            top: Math.max(0, active.y - 12),
                            // Uclarda kart disari tasmasin: hizalamayi kenara gore cevir
                            transform: `translate(${ratio < 0.12 ? '0' : ratio > 0.88 ? '-100%' : '-50%'}, -100%)`,
                        }}
                    >
                        <b>{active.date.format('D MMMM dddd')}</b>
                        <span>
                            {active.count} {t('enw-panel-bookings_short', 'rezervasyon')}
                            {active.revenue && active.revenue.code
                                ? ` · ${formatMoney(active.revenue.amount)} ${active.revenue.code}`
                                : ''}
                        </span>
                    </div>
                )}
            </div>

            <div className={classes.sparkLabels}>
                <span>{points[0].date.format('D MMM')}</span>
                <span>{points[Math.floor(points.length / 3)].date.format('D MMM')}</span>
                <span>{points[Math.floor((points.length * 2) / 3)].date.format('D MMM')}</span>
                <span>{points[points.length - 1].date.format('D MMM')}</span>
            </div>
        </>
    );
}
